import isString from 'lodash/isString';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import first from 'lodash/first';
import allowedTags from '@Services/AllowedTag';
import InnerHTML from '@Components/InnerHTML';

import { ButtonWithIcon, Button } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import { ALLOWED_TAGS_ARRAY } from '../TreeViewConstants';

import './TreeViewListItem.scss';

// Prevent warning from Leyden
// eslint-disable-next-line react/forbid-foreign-prop-types
Checkbox.propTypes.label = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

function TreeViewListItem({ taxonomy, maxLineOfCount, onToggle, onSelect, showCountIndicator = true }) {
  const [tooltip, setTooltip] = useState('');
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const updatedTooltip = taxonomy.name !== 'Other targets' ? '' : 'Targets not yet assigned to a target class';
    setTooltip(updatedTooltip);
  }, [taxonomy]);

  const getPadding = () => {
    const paddingValue = taxonomy.nestingLevel ? `${taxonomy.nestingLevel}rem` : 0;
    return { paddingLeft: paddingValue };
  };

  const getNavigationArrowClass = () => {
    const icons = {
      OPENED: 'ChevronDown',
      CLOSED: 'ChevronRight',
    };
    return taxonomy.expanded && !taxonomy.hasHiddenChildren ? icons.OPENED : icons.CLOSED;
  };

  const getFolderIcon = () => {
    const icons = {
      0: 'folder-empty',
      1: 'folder-half',
      2: 'folder-full',
    };
    return icons[taxonomy.state || 0];
  };

  const getAllowedTags = (value) => allowedTags(value, ALLOWED_TAGS_ARRAY);

  const getHitCountPercentage = () => `${((taxonomy.hitCount / maxLineOfCount) * 100).toFixed(1)}%`;

  const convertToNumber = (hitCount) => {
    if (!hitCount) {
      return null;
    }
    const num = isString(hitCount) ? parseFloat(hitCount) : hitCount;
    if (Number.isNaN(num)) {
      return hitCount;
    }
    return Math.trunc(num)?.toLocaleString('en-US');
  };

  const getAriaChecked = (state) => {
    if (state === 0) return false;
    if (state === 1) return 'mixed';
    return true;
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(taxonomy);
  };

  const handleFolderSelect = () => {
    const isCurrentlySelected = taxonomy.state === 1 || taxonomy.state === 2;
    setAnnouncement(`${taxonomy.name} ${isCurrentlySelected ? 'deselected' : 'selected'}`);
    onSelect(taxonomy);
  };

  const handleLeafSelect = () => {
    setAnnouncement(`${taxonomy.name} ${taxonomy.selected ? 'deselected' : 'selected'}`);
    onSelect(taxonomy);
  };

  // --- Key Handlers for Tree Navigation ---
  const handleArrowRight = (e) => {
    e.preventDefault();
    if (taxonomy.isFolder && (!taxonomy.expanded || taxonomy.hasHiddenChildren)) {
      onToggle(taxonomy);
    } else if (taxonomy.isFolder && taxonomy.expanded) {
      const nextItem = e.currentTarget.querySelector('[role="treeitem"]');
      if (nextItem) nextItem.focus();
    }
  };

  const handleArrowLeft = (e) => {
    e.preventDefault();
    if (taxonomy.isFolder && taxonomy.expanded && !taxonomy.hasHiddenChildren) {
      onToggle(taxonomy);
    } else {
      const parentItem = e.currentTarget.parentElement.closest('[role="treeitem"]');
      if (parentItem) parentItem.focus();
    }
  };

  const handleArrowDown = (e) => {
    e.preventDefault();
    const treeItems = Array.from(document.querySelectorAll('[role="treeitem"]'));
    const currentIndex = treeItems.indexOf(e.currentTarget);
    if (currentIndex > -1 && currentIndex < treeItems.length - 1) {
      treeItems[currentIndex + 1].focus();
    }
  };

  const handleArrowUp = (e) => {
    e.preventDefault();
    const treeItems = Array.from(document.querySelectorAll('[role="treeitem"]'));
    const currentIndex = treeItems.indexOf(e.currentTarget);
    if (currentIndex > 0) {
      treeItems[currentIndex - 1].focus();
    }
  };

  const handleEnterOrSpace = () => {
    if (taxonomy.isFolder) {
      handleFolderSelect();
    } else {
      handleLeafSelect();
    }
  };

  const keyHandlerMap = {
    ArrowRight: handleArrowRight,
    ArrowLeft: handleArrowLeft,
    ArrowDown: handleArrowDown,
    ArrowUp: handleArrowUp,
    Enter: handleEnterOrSpace,
    ' ': handleEnterOrSpace,
  };

  const handleKeyDown = (e) => {
    if (Object.prototype.hasOwnProperty.call(keyHandlerMap, e.key)) {
      e.stopPropagation();
      keyHandlerMap[e.key](e);
    }
  };

  const getContent = (prefixNode) => {
    const synonymHtml =
      Array.isArray(taxonomy.matchedSynonyms) && taxonomy.matchedSynonyms.length > 0
        ? `(<mark>${getAllowedTags(first(taxonomy.matchedSynonyms))}</mark>)`
        : null;

    return (
      <>
        {prefixNode}
        <span data-e2e="taxonomy-label">{getAllowedTags(taxonomy.name)}</span>
        {synonymHtml && (
          <>
            &nbsp;
            <InnerHTML
              tag="span"
              html={synonymHtml}
              className="u-els-color-text u-els-italic"
              data-e2e="taxonomy-hit-synonym"
              allowedTags={[...ALLOWED_TAGS_ARRAY, 'mark']}
            />
          </>
        )}
      </>
    );
  };

  return (
    // The <li> is the treeitem — it owns all ARIA state (expanded, checked, level).
    // aria-expanded is undefined for leaves (they cannot be expanded).
    <li
      role="treeitem"
      data-testid="tree-view-list-item"
      aria-selected={taxonomy.state === 1 || taxonomy.state === 2}
      aria-expanded={taxonomy.isFolder ? (taxonomy.expanded ?? false) : undefined}
      aria-level={(taxonomy.nestingLevel ?? 0) + 1}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="treeViewListItem__wrapper">
      <div
        className={classnames('treeViewListItem', {
          'treeViewListItem-final': !taxonomy.isFolder,
        })}>
        <div
          data-testid="tree-view-list-item-content-wrapper"
          className="treeViewListItem__item"
          title={taxonomy.nameSynonyms ? taxonomy.nameSynonyms?.join('; ') : tooltip}
          style={getPadding()}>
          {taxonomy.isFolder && (
            <>
              <div className="treeViewListItem__item_navigate" data-testid="tree-view-list-item-navigate">
                <ButtonWithIcon
                  data-e2e="tree-view-list-item-toggle"
                  className="u-els-margin-1o4 e2e-tree-view-list-item-toggle"
                  sprite={getNavigationArrowClass()}
                  linkIconSize="xs"
                  iconOnly
                  type="link"
                  onClick={handleToggle}
                  aria-describedby={`taxonomy-${taxonomy.id}`}
                  tabIndex={-1}>
                  {taxonomy.expanded && !taxonomy.hasHiddenChildren ? 'Collapse' : 'Expand'}
                </ButtonWithIcon>
              </div>
              <Button
                id={`taxonomy-${taxonomy.id}`}
                data-e2e="tree-view-list-item-content"
                type="link"
                role="checkbox"
                aria-checked={getAriaChecked(taxonomy.state)}
                className={classnames('treeViewListItem__item_select u-els-color-text', {
                  'treeViewListItem__item_select-selected': taxonomy.state === 1 || taxonomy.state === 2,
                })}
                onClick={handleFolderSelect}
                tabIndex={-1}>
                {getContent(
                  <svg className="folder-icon" focusable={false}>
                    <use xlinkHref={`#icon-${getFolderIcon()}`} />
                  </svg>
                )}
              </Button>
            </>
          )}

          {!taxonomy.isFolder && (
            <Checkbox
              id={`taxonomy-${taxonomy.id}`}
              data-e2e="tree-view-list-item-selectLeaf"
              label={getContent()}
              onChange={handleLeafSelect}
              checked={taxonomy.selected || false}
              tabIndex={-1}
            />
          )}
          <span aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </span>
        </div>

        <div className="treeViewListItem__count">
          <div className="treeViewListItem__count_indicator">
            {showCountIndicator && (
              <>
                <div className="treeViewListItem__count_indicator-full" />
                <div
                  className={classnames('treeViewListItem__count_indicator-full treeViewListItem__count_indicator-current', {
                    'treeViewListItem__count_indicator-current-selected': taxonomy.state === 1 || taxonomy.state === 2,
                  })}
                  style={{ width: getHitCountPercentage() }}
                  data-testid="tree-view-list-item-count-indicator-current"
                />
              </>
            )}
          </div>
          <span className="treeViewListItem__count_badge" data-testid="tree-view-list-item-count-badge">
            {convertToNumber(taxonomy.hitCount)}
          </span>
        </div>
      </div>

      {taxonomy.isFolder && taxonomy.expanded && !taxonomy.hasHiddenChildren && taxonomy.children?.length > 0 && (
        <ul role="group" className="treeViewList__group">
          {taxonomy.children?.map((child) => (
            <TreeViewListItem
              key={`taxonomy-${child.id}-${child.name}`}
              onToggle={onToggle}
              onSelect={onSelect}
              taxonomy={child}
              maxLineOfCount={maxLineOfCount}
              showCountIndicator={showCountIndicator}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

TreeViewListItem.propTypes = {
  showCountIndicator: PropTypes.bool,
  taxonomy: PropTypes.object,
  maxLineOfCount: PropTypes.number,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default TreeViewListItem;
